import moment from 'moment';
import {User} from '../../../../../backend/interfaces/user';
import {EntityReportsRepository} from '../../../../../repositories/entity-reports';
import {createUsersFilters} from '../../../../../components/users-filters/users-filters-utils';

export type ExportCareAndActivitiesReportData = {
  reportData: any[];
};

export async function exportCareAndActivitiesGetReportData({
  entityId,
  senior,
  rawFilters,
  startDate,
  endDate,
}: {
  entityId?: string;
  senior?: User;
  rawFilters: string;
  startDate: string;
  endDate: string;
}): Promise<ExportCareAndActivitiesReportData | null> {
  if (!entityId || !senior?.id) {
    return null;
  }

  const reportFilters = createCareTasksReportFilters(rawFilters);

  const requestReportData = await EntityReportsRepository.instance.careTasks({
    entityId,
    body: {
      userIds: [senior.id],
      entityShiftIds: reportFilters.preFlight.entityShiftIds,
      activitiesGroupIds: reportFilters.preFlight.activitiesGroupIds,
      startsAtDateRange: {
        from: startDate,
        to: endDate,
      },
      propertiesToInclude: [
        'userId',
        'startsAt',
        'markedAsDone',
        'markedAsHandled',
        'markedAsHandledByUserId',
        'notes',
        'feature',
        'data',
        'taskNotDoneReason',
      ],
      activitiesSectors: {
        propertiesToInclude: ['name'],
      },
      activityTypes: {
        propertiesToInclude: ['name'],
      },
      activitiesGroups: {
        propertiesToInclude: ['name'],
      },
      markedAsHandledByUsers: {
        propertiesToInclude: ['name', 'displayName', 'picture'],
      },
    },
  });

  return {
    reportData: filterReportData(requestReportData, reportFilters.postFlight),
  };
}

function createCareTasksReportFilters(rawFilters: string) {
  const filters = createUsersFilters(rawFilters);

  let activitiesGroupIds: string[] | undefined = [
    ...(filters.badl ?? []),
    ...(filters.iadl ?? []),
    ...(filters.clinicalSupport ?? []),
    ...(filters.personalDevelopment ?? []),
  ];
  if (activitiesGroupIds.length === 0) {
    activitiesGroupIds = undefined;
  }

  const entityShiftIds = filters.shifts;

  const statusPlanned = filters.taskStatus?.includes('planned') ? true : undefined;
  const statusNotHandled = filters.taskStatus?.includes('not_handled') ? true : undefined;
  const statusDone = filters.taskStatus?.includes('done') ? true : undefined;
  const statusNotDone = filters.taskStatus?.includes('not_done') ? true : undefined;
  const statusHandled = filters.taskStatus?.includes('handled') ? true : undefined;

  return {
    preFlight: {
      activitiesGroupIds,
      entityShiftIds,
    },
    postFlight: {
      status: {
        planned: statusPlanned,
        not_handled: statusNotHandled,
        done: statusDone,
        not_done: statusNotDone,
        handled: statusHandled,
      },
    },
  };
}

function filterReportData(
  reportData: any[],
  filters: {
    status: {
      planned?: boolean;
      handled?: boolean;
      done?: boolean;
      not_done?: boolean;
      not_handled?: boolean;
    };
  }
): any[] {
  const result: any[] = [];
  const todayDate = moment();

  for (const entry of reportData) {
    const date = moment(entry.startsAt);
    const markedAsHandled = entry.markedAsHandled;
    const markedAsDone = entry.markedAsDone;
    const unattended = !markedAsHandled && date.isBefore(todayDate);
    const planned = !unattended && !markedAsHandled;

    if (filters.status.planned && planned === true) {
      result.push(entry);
      continue;
    }

    if (filters.status.handled && markedAsHandled === true) {
      result.push(entry);
      continue;
    }

    if (filters.status.not_done && markedAsDone === false) {
      result.push(entry);
      continue;
    }

    if (filters.status.done && markedAsDone === true) {
      result.push(entry);
      continue;
    }

    if (filters.status.not_handled && unattended === true) {
      result.push(entry);
    }
  }

  return result.length > 0 ? result : reportData;
}
